import { useState } from "react";
import { Can } from "@core/permissions";
import { useSession } from "@core/session/SessionProvider";
import { ApiError } from "@core/http";
import { tokens } from "@core/theme/tokens";
import { tableSpacing } from "@core/table";
import {
  AlertText,
  Checkbox,
  DialogHeader,
  Field,
  FeedbackState,
  inputStyle,
  Modal,
  PortalButton,
  RowIconAction,
  selectStyle,
  SidePanel,
  StatusText,
  useToast,
} from "@portal/common";
import { markErrorHandled, reportError } from "@portal/common/errorMessage";
import type {
  CatalogEntry,
  Member,
  MemberInput,
  MemberSummary,
  MemberUpdateInput,
  ActiveFilter,
  InvitationStatus,
} from "./api";
import { MEMBER_ERROR_MESSAGES, resolveCatalogId, toCreateInput } from "./api";
import { MemberForm } from "./MemberForm";
import { VehiclesPanel } from "../vehicles";
import { MemberMaintenancesPanel } from "../maintenances";
import { PointsPanel } from "../points";
import { RedemptionsPanel } from "../redemptions";
import {
  useAdvisors,
  useMember,
  useMemberCatalog,
  useMemberMutations,
  useMembers,
  useMembersOverallTotal,
} from "./memberQueries";

const INVITATION: Record<MemberSummary["invitationStatus"], string> = {
  not_invited: "Sin invitar",
  invited: "Invitado",
  registered: "Registrado",
  expired: "Expirada",
};
// SPEC-202 § «Inactivación» / criterio 10: el saldo y el avance que se
// perderán solo son autoritativos cuando vienen del `409 CONFIRM_INACTIVATE`
// de ese instante (api/src/management/members/member.service.ts:270-307), no
// de `MemberSummary.pointsBalance` cacheado. El backend devuelve
// `currentProgress`/`goal` en `null` cuando el miembro no tiene conteo del
// Gran Premio abierto (sin backfill histórico) — `null` es "no lo sabemos",
// nunca "cero"; aplastarlo a un número fabricaría una cifra en una
// advertencia de pérdida irreversible.
interface InactivationImpact {
  balanceToSubtract: number;
  currentProgress: number | null;
  goal: number | null;
}
// SPEC-202 §Ficha / B-013: datos de la segunda fila de la cabecera. `goal`
// llega en 0 (o ausente) cuando no hay Gran Premio activo: el avance es "—",
// nunca "0 / 0". `pointsBalance` ausente también cae a "—", no a 0.
const memberHeaderFacts = (member: Member): [string, string][] => [
  ["Membresía", member.membershipNumber],
  [
    "Saldo",
    member.pointsBalance === undefined
      ? "—"
      : `${member.pointsBalance.toLocaleString("es-EC")} pts`,
  ],
  ["Avance", member.goal ? `${member.progress ?? 0} / ${member.goal}` : "—"],
  ["Asesor", member.advisor?.name ?? "—"],
];
const asNumber = (value: unknown): number =>
  typeof value === "number" && Number.isFinite(value) ? value : 0;
const asNullableNumber = (value: unknown): number | null =>
  typeof value === "number" && Number.isFinite(value) ? value : null;
const phone = (m: MemberSummary) => {
  // El contrato vigente no declara este campo, pero la API puede incluirlo.
  const value = Reflect.get(m, "phone");
  return typeof value === "string" && value.trim() ? value : "—";
};
const invitationTone: Record<MemberSummary["invitationStatus"], string> = {
  not_invited: "neutral",
  invited: "silver",
  registered: "gold",
  expired: "expired",
};

const iconPaths = {
  send: "M4 12l16-7-7 16-2-7-7-2z",
  plus: "M12 5v14 M5 12h14",
  invite: "M16 11a4 4 0 10-8 0 M3 21a7 7 0 0114 0 M19 8v6 M22 11h-6",
} as const;

function PrototypeIcon({
  name,
  size = 22,
  stroke = 1.5,
}: {
  name: keyof typeof iconPaths;
  size?: number;
  stroke?: number;
}) {
  return (
    <svg
      aria-hidden="true"
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={stroke}
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      {iconPaths[name].split(" M").map((segment, index) => (
        <path key={index} d={index === 0 ? segment : `M${segment}`} />
      ))}
    </svg>
  );
}

// SPEC-202, bug QA-MTZ-002 #3/#4/#6 + hallazgo de pérdida silenciosa:
// `MemberForm` reenvía en el `PUT` cualquier id que no reciba acá, y
// `member.repository.ts` interpreta un id ausente como "desconectar" (borra
// ciudad, asesor y taller preferido en cada edición). `idTypeId` y
// `advisorId` ya llegan resueltos del DTO (uuid, no etiqueta); ciudad y
// taller preferido llegan como etiqueta (`city`/`preferredWorkshop`) y se
// resuelven contra el catálogo ya cargado, igual que
// `vehicles/api.ts#resolveCatalogId`. Antes vivía inline en el modal de
// edición; ahora la usa el formulario siempre visible de la pestaña "Datos
// generales" (finding de diseño de la ola 2: la edición no puede pasar por
// un modal aparte).
function memberFormInitial(
  member: Member,
  cities: CatalogEntry[],
  workshops: CatalogEntry[],
): Partial<MemberInput> {
  return {
    idTypeId: member.idTypeId ?? "",
    identification: member.identification ?? "",
    legalName: member.legalName,
    email: member.email,
    phone: member.phone ?? "",
    cityId: resolveCatalogId(cities, member.city),
    // El DTO ya emite `advisorId` (uuid de `staff_user`) directo —
    // `api-advisor-domain`/`api-advisor-http` cerraron el hallazgo que
    // obligaba a resolverlo por el nombre de `member.advisor` (siempre
    // vacío, porque el DTO nunca emitía `advisor`). Resolver una identidad
    // por etiqueta es frágil (dos asesores pueden llamarse igual); con el id
    // disponible, tomarlo tal cual es lo correcto, no un atajo.
    advisorId: member.advisorId ?? "",
    preferredWorkshopId: resolveCatalogId(workshops, member.preferredWorkshop),
    purchaseDate: member.purchaseDate ?? null,
    inviteOnCreate: false,
  };
}

export function MembersScreen() {
  const toast = useToast();
  const [search, setSearch] = useState("");
  const [invitation, setInvitation] = useState<InvitationStatus>("all");
  const [active, setActive] = useState<ActiveFilter>("all");
  const [pageIndex, setPageIndex] = useState(0);
  const [selected, setSelected] = useState<string[]>([]);
  // SPEC-202, findings de diseño de la ola 2: la edición ya no pasa por un
  // modal separado (viola "always visible" del contrato `portal.member-data`
  // y el criterio del prototipo, donde el formulario vive siempre dentro de
  // la pestaña "Datos generales") — este estado ahora solo abre el modal de
  // "Nuevo miembro".
  const [creating, setCreating] = useState(false);
  const [detailId, setDetailId] = useState<string | null>(null);
  const [tab, setTab] = useState("data");
  const [inactivate, setInactivate] = useState<MemberSummary | null>(null);
  const [reason, setReason] = useState("");
  const [inactivateImpact, setInactivateImpact] =
    useState<InactivationImpact | null>(null);
  // QA-MTZ-002 #10: mismo criterio que `registerKey`/`voidKey` de
  // `PointsPanel` — la clave se fija una sola vez al abrir el diálogo (acá
  // abajo, en ambos puntos que llaman `setInactivate`) y el reintento
  // confirmado la reusa. Generarla de nuevo en `confirmInactivate` haría que
  // el servidor viera dos operaciones distintas y anularía la idempotencia.
  const [inactivateKey, setInactivateKey] = useState<string | null>(null);
  const [reactivation, setReactivation] = useState<Member | null>(null);
  const [reactivationDate, setReactivationDate] = useState("");
  const [reactivationReason, setReactivationReason] = useState("");
  // Mismo criterio que `CatalogsScreen` (trace f4575316-5c75-4c27-83d0-
  // 340db0aafd9f): el 409 de alta/edición se lo pasamos a `MemberForm` para
  // que intente asociarlo a un campo (`applyApiFieldErrors`,
  // `IDENTIFICATION_DUPLICATE`/`EMAIL_DUPLICATE`); si no puede, nos avisa por
  // `onUnhandledSubmitError` y ahí sí sale el toast de siempre.
  const [submitError, setSubmitError] = useState<unknown>(undefined);
  const members = useMembers(search, invitation, active);
  const overallTotal = useMembersOverallTotal();
  const detail = useMember(detailId);
  const mutations = useMemberMutations();
  const idTypes = useMemberCatalog("id-types");
  const cities = useMemberCatalog("cities");
  const workshops = useMemberCatalog("workshops");
  const inactivationReasons = useMemberCatalog("inactivation-reasons");
  const advisors = useAdvisors();
  const { hasPermission } = useSession();
  const pages = members.data?.pages ?? [];
  const currentPage = pages[pageIndex];
  const rows = currentPage?.items ?? [];
  const last = pages.at(-1);
  const total = last?.meta.total ?? rows.length;
  // CONFLICT-01 (ola 3, resolución humana): admin.jsx:777 solo antepone
  // "X de " cuando el conteo visible difiere del universo completo; con
  // filtros en "todos" ambos números coinciden y no debe aparecer el " de Y".
  // Mientras `overallTotal` sigue cargando, se omite el sufijo en vez de
  // mostrar un número a medio calcular.
  const resultsLabel =
    overallTotal.data !== undefined && overallTotal.data !== total
      ? `${total} de ${overallTotal.data}`
      : `${total}`;
  const canGoBack = pageIndex > 0;
  const canGoForward = pageIndex < pages.length - 1 || Boolean(last?.meta.next);
  const firstVisible = pages
    .slice(0, pageIndex)
    .reduce((count, page) => count + page.items.length, 0);
  const selectAllVisible = () => {
    const visibleIds = rows.map((row) => row.id);
    const allSelected =
      visibleIds.length > 0 && visibleIds.every((id) => selected.includes(id));
    setSelected((current) =>
      allSelected
        ? current.filter((id) => !visibleIds.includes(id))
        : [...new Set([...current, ...visibleIds])],
    );
  };
  const goForward = async () => {
    if (pageIndex < pages.length - 1) {
      setPageIndex((current) => current + 1);
      return;
    }
    if (!last?.meta.next || members.isFetchingNextPage) return;
    await members.fetchNextPage();
    setPageIndex((current) => current + 1);
  };
  const toggle = (id: string) =>
    setSelected((current) =>
      current.includes(id) ? current.filter((x) => x !== id) : [...current, id],
    );
  const openDetail = (id: string, nextTab = "data") => {
    setDetailId(id);
    setTab(nextTab);
  };
  const batchInvite = () =>
    mutations.batchInvite.mutate(selected, {
      onSuccess: (result) => {
        setSelected([]);
        toast.show(
          `Se enviaron ${result.sent} invitaciones. Se omitieron ${result.skipped} miembros ya registrados`,
        );
      },
      onError: (error) => reportError(toast, error, MEMBER_ERROR_MESSAGES),
    });
  // PLAN-009 §3: el error de servicio va siempre al toast, también con el
  // modal de alta abierto — el toast de la fundación se muestra por encima
  // (`values.toast.zIndex`), así que no hace falta un segundo aviso en la
  // pantalla que queda tapada detrás del modal.
  // Hallazgo (revisión en vivo 2026-09-23): `IDENTIFICATION_DUPLICATE`/
  // `EMAIL_DUPLICATE` solo podían detectarse en el servidor; antes salían
  // siempre por este toast genérico (`MEMBER_ERROR_MESSAGES`), sin marcar el
  // campo. Ahora el error se le pasa a `MemberForm` (`submitError`), que
  // intenta asociarlo a `identification`/`email` — el toast queda como
  // respaldo (`onUnhandledSubmitError`) para lo que el formulario no pueda
  // asociar a un campo propio.
  const handleUnhandledMemberSubmitError = (error: unknown) =>
    reportError(toast, error, MEMBER_ERROR_MESSAGES);
  const saveCreate = (input: MemberUpdateInput) => {
    mutations.create.mutate(toCreateInput(input), {
      onSuccess: () => {
        setCreating(false);
        toast.show("Miembro registrado correctamente.");
      },
      onError: (e) => setSubmitError(e),
    });
  };
  // SPEC-202, finding de diseño de la ola 2: la edición ahora vive siempre
  // visible dentro de la pestaña "Datos generales" (no un modal aparte), así
  // que guarda sin cerrar la ficha — el prototipo (admin.jsx:918-921)
  // tampoco cierra la ficha al guardar, solo muestra el toast.
  const saveEdit = (id: string, input: MemberUpdateInput) =>
    mutations.edit.mutate(
      { id, input },
      {
        onSuccess: () => toast.show("Cambios guardados correctamente."),
        onError: (e) => setSubmitError(e),
      },
    );
  // SPEC-202 § «Inactivación» / contrato `inactivateMember`: la primera
  // llamada va siempre sin `confirmed` (o `false`). El servidor responde
  // `409 CONFIRM_INACTIVATE` con el impacto real — nunca se calcula acá — y
  // solo una segunda confirmación explícita repite la llamada con
  // `confirmed: true`. Mismo patrón de dos pasos que `VehiclesPanel`.
  const confirmInactivate = () => {
    if (!inactivate || !reason) return;
    // `inactivateKey` ya quedó fijado al abrir el diálogo; el fallback solo
    // cubre una apertura que por algún motivo no pasó por ese punto.
    const idempotencyKey = inactivateKey ?? crypto.randomUUID();
    mutations.inactivate.mutate(
      {
        id: inactivate.id,
        input: {
          inactivationReasonId: reason,
          confirmed: Boolean(inactivateImpact),
        },
        idempotencyKey,
      },
      {
        onSuccess: () => {
          setInactivate(null);
          setInactivateImpact(null);
          setInactivateKey(null);
          toast.show("Membresía inactivada.");
        },
        onError: (e) => {
          if (e instanceof ApiError && e.code === "CONFIRM_INACTIVATE") {
            const raw = (e.problem?.impact ?? {}) as Record<string, unknown>;
            setInactivateImpact({
              balanceToSubtract: asNumber(raw.balanceToSubtract),
              currentProgress: asNullableNumber(raw.currentProgress),
              goal: asNullableNumber(raw.goal),
            });
            markErrorHandled(e);
            return;
          }
          reportError(toast, e, MEMBER_ERROR_MESSAGES);
        },
      },
    );
  };
  return (
    <div data-testid="members-visual-content">
      <header style={{ marginBottom: 0 }}>
        <h1 style={{ margin: 0, font: `300 34px ${tokens.font.display}` }}>
          Gestión de miembros
        </h1>
        <p
          style={{
            color: tokens.text.tertiary,
            margin: "2px 0 26px",
            fontSize: 13,
          }}
        >
          Propietarios Mercedes-Benz, sus vehículos y el estado de sus
          invitaciones.
        </p>
      </header>
      <div data-testid="members-list-content">
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 12,
            minHeight: 44,
            padding: 14,
            marginBottom: 16,
            border: `1px solid ${tokens.surface.line}`,
            borderRadius: 18,
            background: tokens.surface.bg2,
          }}
        >
          <input
            aria-label="Buscar por nombre, identificación o correo"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setSelected([]);
              setPageIndex(0);
            }}
            placeholder="Buscar por nombre, identificación o correo"
            style={{
              flex: 1,
              minWidth: 240,
              maxWidth: 380,
              padding: "11px 13px",
              color: tokens.text.primary,
              background: tokens.surface.surface,
              border: `1px solid ${tokens.surface.line2}`,
              borderRadius: 11,
              font: `400 13.5px ${tokens.font.ui}`,
            }}
          />
          <select
            aria-label="Estado de invitación"
            value={invitation}
            style={{
              minWidth: 150,
              padding: "11px 13px",
              color: tokens.text.primary,
              background: tokens.surface.surface,
              border: `1px solid ${tokens.surface.line2}`,
              borderRadius: 11,
              font: `400 13.5px ${tokens.font.ui}`,
            }}
            onChange={(e) => {
              setInvitation(e.target.value as InvitationStatus);
              setSelected([]);
              setPageIndex(0);
            }}
          >
            <option value="all">Invitación · todas</option>
            {Object.entries(INVITATION).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
          <select
            aria-label="Estado del miembro"
            value={active}
            style={{
              minWidth: 132,
              padding: "11px 13px",
              color: tokens.text.primary,
              background: tokens.surface.surface,
              border: `1px solid ${tokens.surface.line2}`,
              borderRadius: 11,
              font: `400 13.5px ${tokens.font.ui}`,
            }}
            onChange={(e) => {
              setActive(e.target.value as ActiveFilter);
              setSelected([]);
              setPageIndex(0);
            }}
          >
            <option value="all">Estado · todos</option>
            <option value="active">Activo</option>
            <option value="inactive">Inactivo</option>
          </select>
          <div style={{ flex: 1 }} />
          <Can module="members" action="canEdit">
            <button className="btn btn-ghost btn-sm" onClick={batchInvite}>
              <PrototypeIcon name="send" size={14} />
              Invitar seleccionados
              {selected.length ? ` (${selected.length})` : ""}
            </button>
          </Can>
          <Can module="members" action="canCreate">
            <button
              className="btn btn-primary btn-sm"
              onClick={() => setCreating(true)}
            >
              <PrototypeIcon name="plus" size={15} />
              Nuevo miembro
            </button>
          </Can>
        </div>
        {members.isLoading ? (
          <FeedbackState kind="loading" />
        ) : members.isError ? (
          <FeedbackState kind="error" />
        ) : rows.length ? (
          <div
            style={{
              border: `1px solid ${tokens.surface.line}`,
              borderRadius: 18,
              overflow: "hidden",
              // `minHeight`, no `height`: la tarjeta conserva el alto de 8
              // filas (PER=8, admin.jsx:701; la API recibe `limit=8`) pero
              // ya no recorta la barra de desplazamiento horizontal ni una
              // página más alta (QA-MTZ-002 #3).
              minHeight: 621,
              background: tokens.surface.bg2,
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "18px 24px 14px",
              }}
            >
              <span className="label">Miembros del programa</span>
              <span
                style={{
                  font: `500 11.5px ${tokens.font.ui}`,
                  color: tokens.text.tertiary,
                }}
              >
                {resultsLabel} resultados
              </span>
            </div>
            {/* QA-MTZ-002 #3: `overflowX: "auto"` como el prototipo
              (admin.jsx:868-869); con `overflow: "hidden"` las columnas que no
              caben en 1180px quedaban recortadas sin forma de verlas. */}
            <div style={{ overflowX: "auto" }}>
              <div style={{ minWidth: 1180 }}>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns:
                      "30px 1.05fr 1.55fr 1.6fr 1.05fr 52px 108px 92px 216px",
                    gap: 14,
                    alignItems: "center",
                    padding: "0 24px 13px",
                    borderBottom: `1px solid ${tokens.surface.line}`,
                  }}
                >
                  <Checkbox
                    ariaLabel="Seleccionar todos"
                    checked={
                      rows.length > 0 &&
                      rows.every((row) => selected.includes(row.id))
                    }
                    onChange={selectAllVisible}
                  />
                  {[
                    "Identificación",
                    "Nombres",
                    "Correo",
                    "Teléfono",
                    "Veh.",
                    "Invitación",
                    "Estado",
                  ].map((header) => (
                    <span key={header} className="label">
                      {header}
                    </span>
                  ))}
                  <span className="label" style={{ textAlign: "right" }}>
                    Acciones
                  </span>
                </div>
                {rows.map((m) => {
                  const initials = m.legalName
                    .split(/\s+/)
                    .slice(0, 2)
                    .map((part) => part[0])
                    .join("")
                    .toUpperCase();
                  return (
                    <div
                      key={m.id}
                      style={{
                        display: "grid",
                        gridTemplateColumns:
                          "30px 1.05fr 1.55fr 1.6fr 1.05fr 52px 108px 92px 216px",
                        gap: 14,
                        alignItems: "center",
                        padding: "13px 24px",
                        borderBottom: `1px solid ${tokens.surface.line}`,
                        opacity: m.active ? 1 : 0.55,
                      }}
                    >
                      <Checkbox
                        ariaLabel={`Seleccionar ${m.legalName}`}
                        checked={selected.includes(m.id)}
                        onChange={() => toggle(m.id)}
                      />
                      <span
                        style={{
                          font: `400 12.5px ${tokens.font.ui}`,
                          color: tokens.text.secondary,
                        }}
                      >
                        {m.identification ?? "—"}
                      </span>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          minWidth: 0,
                          gap: 10,
                        }}
                      >
                        <div
                          className="av"
                          style={{ width: 30, height: 30, fontSize: 10.5 }}
                        >
                          {initials}
                        </div>
                        <div style={{ minWidth: 0 }}>
                          <strong
                            style={{
                              display: "block",
                              font: `600 12.5px ${tokens.font.ui}`,
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                            }}
                          >
                            {m.legalName}
                          </strong>
                          <span
                            style={{
                              display: "block",
                              marginTop: 2,
                              font: `400 10.5px ${tokens.font.ui}`,
                              color: tokens.text.disabled,
                            }}
                          >
                            {m.city ?? "—"}
                          </span>
                        </div>
                      </div>
                      <span
                        style={{ color: tokens.text.secondary, fontSize: 12.5 }}
                      >
                        {m.email}
                      </span>
                      <span
                        style={{ color: tokens.text.secondary, fontSize: 12.5 }}
                      >
                        {phone(m)}
                      </span>
                      <strong style={{ font: `600 12.5px ${tokens.font.ui}` }}>
                        {m.activeVehicles ?? 0}
                      </strong>
                      <span>
                        <span
                          className={
                            m.invitationStatus === "expired"
                              ? undefined
                              : `stat stat-${invitationTone[m.invitationStatus]}`
                          }
                          style={
                            m.invitationStatus === "expired"
                              ? {
                                  font: `600 10.5px/1 ${tokens.font.ui}`,
                                  letterSpacing: "0.12em",
                                  textTransform: "uppercase",
                                  borderRadius: 999,
                                  padding: "6px 11px",
                                  display: "inline-flex",
                                  alignItems: "center",
                                  whiteSpace: "nowrap",
                                  background: "rgba(214,124,104,0.13)",
                                  color: tokens.danger.base,
                                  border: "1px solid rgba(214,124,104,0.34)",
                                }
                              : undefined
                          }
                        >
                          {INVITATION[m.invitationStatus]}
                        </span>
                      </span>
                      <span>
                        <span
                          className={`stat stat-${m.active ? "silver" : "neutral"}`}
                        >
                          {m.active ? "Activo" : "Inactivo"}
                        </span>
                      </span>
                      {/* Revisión en vivo 2026-09-22, punto #10: tres acciones
                        por fila con tres formas distintas — "Ver" y "Enviar
                        invitación" eran `.btn-link` (texto plano, sin caja) y
                        la tercera un `.chip`. "Enviar invitación" sola mide
                        ~120px de texto, así que el grupo era también el más
                        ancho de la pantalla. Las dos primeras pasan a ícono +
                        tooltip; la tercera ya era un chip con un glifo. El
                        `gap` pasa de 4 a `tableSpacing.actionGap`, el mismo
                        de todas las demás filas del portal. */}
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: tableSpacing.actionGap,
                          justifyContent: "flex-end",
                        }}
                      >
                        <RowIconAction
                          label="Ver"
                          icon="eye"
                          onClick={() => openDetail(m.id)}
                        />
                        <Can module="members" action="canEdit">
                          <RowIconAction
                            label="Enviar invitación"
                            icon="invite"
                            onClick={() =>
                              mutations.invite.mutate(m.id, {
                                onSuccess: () =>
                                  toast.show("Invitación enviada."),
                                onError: (e) =>
                                  reportError(toast, e, MEMBER_ERROR_MESSAGES),
                              })
                            }
                          />
                        </Can>
                        {m.active ? (
                          <Can module="members" action="canInactivate">
                            <RowIconAction
                              label="Inactivar"
                              icon="deactivate"
                              tone="danger"
                              onClick={() => {
                                setInactivate(m);
                                setReason("");
                                setInactivateImpact(null);
                                setInactivateKey(crypto.randomUUID());
                              }}
                            />
                          </Can>
                        ) : null}
                      </div>
                    </div>
                  );
                })}
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    padding: "15px 24px 13px",
                  }}
                >
                  <span
                    style={{
                      font: `400 11.5px ${tokens.font.ui}`,
                      color: tokens.text.tertiary,
                    }}
                  >
                    Mostrando {rows.length ? firstVisible + 1 : 0}–
                    {firstVisible + rows.length} de {total}
                  </span>
                  <div style={{ display: "flex", gap: 6 }}>
                    <button
                      className="chip"
                      disabled={!canGoBack}
                      onClick={() => setPageIndex((current) => current - 1)}
                    >
                      Anterior
                    </button>
                    {pages.map((_, index) => (
                      <button
                        key={index}
                        className={`chip${index === pageIndex ? " on" : ""}`}
                        onClick={() => setPageIndex(index)}
                      >
                        {index + 1}
                      </button>
                    ))}
                    {pages.length === 1 && canGoForward ? (
                      <button className="chip" onClick={() => void goForward()}>
                        2
                      </button>
                    ) : null}
                    <button
                      className="chip"
                      disabled={!canGoForward || members.isFetchingNextPage}
                      onClick={() => void goForward()}
                    >
                      Siguiente
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div
            style={{
              border: `1px solid ${tokens.surface.line}`,
              borderRadius: 18,
              height: 387,
              background: tokens.surface.bg2,
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "18px 24px 14px",
              }}
            >
              <span className="label">Miembros del programa</span>
              <span
                style={{
                  font: `500 11.5px ${tokens.font.ui}`,
                  color: tokens.text.tertiary,
                }}
              >
                {/* SPEC-202, finding de la ola 2 (diseño): el contador
                    llevaba el literal fijo "0 de 14 resultados" sin importar
                    el total real devuelto por `meta.total`. El prototipo
                    (admin.jsx:773) lo calcula siempre a partir de los datos,
                    nunca un literal — mismo criterio que la fila poblada de
                    esta pantalla (`{resultsLabel} resultados`, arriba).
                    CONFLICT-01 (ola 3): `total` es siempre el conteo
                    FILTRADO (`meta.total` del listado con los filtros
                    activos); sin el universo sin filtrar de
                    `useMembersOverallTotal` este estado vacío nunca podía
                    mostrar "0 de 1 resultados" — se quedaba en "0
                    resultados" sin importar cuántos miembros hubiera en
                    total. */}
                {resultsLabel} resultados
              </span>
            </div>
            <div style={{ padding: "63px 24px 76px", textAlign: "center" }}>
              <div
                className="av"
                style={{
                  width: 54,
                  height: 54,
                  margin: "0 auto 16px",
                  color: tokens.text.tertiary,
                }}
              >
                <PrototypeIcon name="invite" size={22} stroke={1.4} />
              </div>
              <h3
                className="display"
                style={{ fontSize: 22, lineHeight: 1.1, margin: "0 0 8px" }}
              >
                Ningún miembro coincide
              </h3>
              <div
                style={{
                  maxWidth: 420,
                  margin: "0 auto 20px",
                  font: `400 13px/1.6 ${tokens.font.ui}`,
                  color: tokens.text.tertiary,
                }}
              >
                Ajuste la búsqueda o los filtros, o registre al primer
                propietario Mercedes-Benz para empezar a enviar invitaciones.
              </div>
              <div
                style={{ display: "flex", justifyContent: "center", gap: 10 }}
              >
                <button
                  className="btn btn-ghost btn-sm"
                  onClick={() => {
                    setSearch("");
                    setInvitation("all");
                    setActive("all");
                    setPageIndex(0);
                  }}
                >
                  Limpiar filtros
                </button>
                <button
                  className="btn btn-primary btn-sm"
                  onClick={() => setCreating(true)}
                >
                  <PrototypeIcon name="plus" size={15} />
                  Nuevo miembro
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
      {creating ? (
        <MemberForm
          editing={false}
          modal={{
            label: "Nuevo miembro",
            width: 720,
            onClose: () => setCreating(false),
          }}
          onSubmit={saveCreate}
          {...(idTypes.data ? { idTypes: idTypes.data } : {})}
          {...(cities.data ? { cities: cities.data } : {})}
          {...(advisors.data ? { advisors: advisors.data } : {})}
          onCancel={() => setCreating(false)}
          submitting={mutations.create.isPending}
          submitError={submitError}
          onUnhandledSubmitError={handleUnhandledMemberSubmitError}
        />
      ) : null}
      {detailId ? (
        <SidePanel label="Ficha del miembro" onClose={() => setDetailId(null)}>
          {detail.isLoading ? (
            <div style={{ padding: 24 }}>Cargando…</div>
          ) : detail.data ? (
            <>
              {/* Cabecera del panel — admin.jsx:670-682 (SheetHead) y su uso
                  en :869-887. Antetítulo, nombre con chips de invitación y
                  estado, fila de metadatos (identificación, correo, teléfono,
                  cantidad de vehículos) y las acciones "Enviar
                  invitación"/"Inactivar-Reactivar" a la derecha, reproducidos
                  contra el baseline canónico
                  portal-member-vehicles-default@1440x900.png.
                  CONFLICT-01 (resuelto: B-013, decisión del equipo del
                  2026-10-05, PLAN-010 P12): la cabecera suma, en una
                  segunda fila de metadatos con el mismo estilo que la
                  primera (`memberHeaderFacts`), el número de membresía, el
                  saldo de puntos, el avance sobre la meta del Gran Premio
                  y el nombre del asesor asignado (sin su taller, P5). El
                  prototipo no dibuja estos datos: no se creó ningún
                  estilo ni primitiva nueva, se reusa la convención de la
                  fila de identificación/correo/teléfono/vehículos, sin
                  reordenar ni reestilar lo existente. Sin dato (`advisor`
                  nulo, sin Gran Premio activo → `goal` 0 o ausente) se
                  pinta "—", el mismo respaldo neutro de esa fila. */}
              <div
                style={{
                  padding: "22px 26px 18px",
                  borderBottom: `1px solid ${tokens.surface.line}`,
                  display: "flex",
                  alignItems: "flex-start",
                  gap: 16,
                }}
              >
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div className="label" style={{ marginBottom: 7 }}>
                    Ficha del miembro
                  </div>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 12,
                      flexWrap: "wrap",
                    }}
                  >
                    <h2 className="display" style={{ fontSize: 27, margin: 0 }}>
                      {detail.data.legalName}
                    </h2>
                    <span
                      className={
                        detail.data.invitationStatus === "expired"
                          ? undefined
                          : `stat stat-${invitationTone[detail.data.invitationStatus]}`
                      }
                      style={
                        detail.data.invitationStatus === "expired"
                          ? {
                              font: `600 10.5px/1 ${tokens.font.ui}`,
                              letterSpacing: "0.12em",
                              textTransform: "uppercase",
                              borderRadius: 999,
                              padding: "6px 11px",
                              display: "inline-flex",
                              alignItems: "center",
                              whiteSpace: "nowrap",
                              background: "rgba(214,124,104,0.13)",
                              color: tokens.danger.base,
                              border: "1px solid rgba(214,124,104,0.34)",
                            }
                          : undefined
                      }
                    >
                      {INVITATION[detail.data.invitationStatus]}
                    </span>
                    <span
                      className={`stat stat-${detail.data.active ? "silver" : "neutral"}`}
                    >
                      {detail.data.active ? "Activo" : "Inactivo"}
                    </span>
                  </div>
                  <div
                    style={{
                      display: "flex",
                      gap: 20,
                      flexWrap: "wrap",
                      marginTop: 8,
                      font: `400 12px ${tokens.font.ui}`,
                      color: tokens.text.tertiary,
                    }}
                  >
                    <span>
                      {/* SPEC-202, bug QA-MTZ-002 #6: el fallback fabricado
                          "Cédula" pintaba el mismo literal para todo miembro
                          sin importar su tipo real de identificación. El DTO
                          ya resuelve `identificationType` contra el
                          catálogo (`api-dto`, con respaldo al id crudo si el
                          catálogo no encuentra la entrada — nunca `null` en
                          un campo que el contrato declara requerido); si de
                          todos modos no llegara, "—" es el mismo fallback
                          neutro que ya usan las demás celdas de esta fila,
                          no un dato inventado. */}
                      {detail.data.identificationType ?? "—"}{" "}
                      {detail.data.identification ?? "—"}
                    </span>
                    <span>{detail.data.email}</span>
                    <span>{detail.data.phone ?? "—"}</span>
                    <span>
                      {detail.data.activeVehicles ?? 0}{" "}
                      {(detail.data.activeVehicles ?? 0) === 1
                        ? "vehículo"
                        : "vehículos"}
                    </span>
                  </div>
                  <div
                    data-testid="member-header-facts"
                    style={{
                      display: "flex",
                      gap: 20,
                      flexWrap: "wrap",
                      marginTop: 6,
                      font: `400 12px ${tokens.font.ui}`,
                      color: tokens.text.tertiary,
                    }}
                  >
                    {memberHeaderFacts(detail.data).map(([label, value]) => (
                      <span key={label}>
                        {label} {value}
                      </span>
                    ))}
                  </div>
                </div>
                <div style={{ display: "flex", gap: 8, flex: "none" }}>
                  <Can module="members" action="canEdit">
                    <RowIconAction
                      label="Enviar invitación"
                      icon="invite"
                      onClick={() =>
                        mutations.invite.mutate(detail.data!.id, {
                          onSuccess: () => toast.show("Invitación enviada."),
                          onError: (e) =>
                            reportError(toast, e, MEMBER_ERROR_MESSAGES),
                        })
                      }
                    />
                  </Can>
                  <Can module="members" action="canInactivate">
                    {detail.data.active ? (
                      <RowIconAction
                        label="Inactivar"
                        icon="deactivate"
                        tone="danger"
                        onClick={() => {
                          setInactivate(detail.data!);
                          setReason("");
                          setInactivateImpact(null);
                          setInactivateKey(crypto.randomUUID());
                        }}
                      />
                    ) : (
                      <RowIconAction
                        label="Reactivar"
                        icon="check"
                        onClick={() => {
                          setReactivation(detail.data!);
                          setReactivationDate("");
                          setReactivationReason("");
                        }}
                      />
                    )}
                  </Can>
                </div>
                <button
                  onClick={() => setDetailId(null)}
                  className="chip"
                  style={{ padding: "8px 11px", flex: "none" }}
                >
                  ✕
                </button>
              </div>
              {/* admin.jsx:888-891: las pestañas de la ficha son chips
                  independientes con `gap: 8` — no el primitivo compartido
                  `SegmentedControl` (un solo contenedor con borde propio,
                  admin.jsx:187-198/tokens de STD-003 para otras pantallas),
                  que produce un look distinto (radio y separación) del que
                  el baseline canónico exige acá. `Vehículos (N)` lleva el
                  conteo del miembro entre paréntesis (admin.jsx:889); el
                  prototipo cuenta el arreglo completo de `VEHS` (activos e
                  inactivos), y el contrato de `MemberSummary`/`Member` solo
                  expone `activeVehicles` — la cifra disponible más cercana,
                  que coincide con el total del baseline porque el miembro
                  sembrado no tiene vehículos inactivos. */}
              <div
                aria-label="Ficha del miembro"
                style={{ display: "flex", gap: 8, padding: "14px 26px 0" }}
              >
                {(
                  [
                    ["data", "Datos generales"],
                    [
                      "vehicles",
                      `Vehículos (${detail.data.activeVehicles ?? 0})`,
                    ],
                    ["maintenance", "Mantenimientos"],
                    ["points", "Puntos"],
                    ["redemptions", "Consumos"],
                  ] as const
                ).map(([value, label]) => (
                  <button
                    key={value}
                    type="button"
                    aria-current={tab === value ? "page" : undefined}
                    className={`chip${tab === value ? " on" : ""}`}
                    onClick={() => setTab(value)}
                  >
                    {label}
                  </button>
                ))}
              </div>
              <div
                style={{
                  flex: 1,
                  overflowY: "auto",
                  padding: "22px 26px 30px",
                }}
              >
                {tab === "data" ? (
                  // SPEC-202, findings de diseño de la ola 2: `screens.json`
                  // declara un único estado `default` para
                  // `portal.member-data` con id-type/id/nombres/correo/
                  // teléfono/ciudad y las acciones «Guardar cambios»/
                  // «Cancelar», todas con visibilidad `always` — igual que
                  // el prototipo (admin.jsx:895-925), donde el formulario
                  // vive siempre visible dentro de esta pestaña, sin una
                  // vista de solo lectura ni un modal aparte. El resumen de
                  // membresía/saldo/avance/asesor vive en la cabecera de
                  // la ficha (B-013, PLAN-010 P12), no en esta pestaña.
                  hasPermission("members", "canEdit") ? (
                    <MemberForm
                      editing
                      initial={memberFormInitial(
                        detail.data,
                        cities.data ?? [],
                        workshops.data ?? [],
                      )}
                      onSubmit={(input) => saveEdit(detail.data!.id, input)}
                      onCancel={() => setDetailId(null)}
                      submitting={mutations.edit.isPending}
                      {...(idTypes.data ? { idTypes: idTypes.data } : {})}
                      {...(cities.data ? { cities: cities.data } : {})}
                      {...(advisors.data ? { advisors: advisors.data } : {})}
                      submitError={submitError}
                      onUnhandledSubmitError={handleUnhandledMemberSubmitError}
                    />
                  ) : (
                    <StatusText>
                      No tiene permiso para editar los datos de este miembro.
                    </StatusText>
                  )
                ) : tab === "vehicles" ? (
                  <VehiclesPanel memberId={detail.data.id} />
                ) : tab === "maintenance" ? (
                  <MemberMaintenancesPanel memberId={detail.data.id} />
                ) : tab === "points" ? (
                  <PointsPanel
                    memberId={detail.data.id}
                    memberName={detail.data.legalName}
                  />
                ) : tab === "redemptions" ? (
                  <RedemptionsPanel
                    memberId={detail.data.id}
                    memberName={detail.data.legalName}
                  />
                ) : (
                  <StatusText>
                    Este destino se implementa en su SPEC correspondiente.
                  </StatusText>
                )}
              </div>
            </>
          ) : (
            <div style={{ padding: 24 }}>
              <FeedbackState kind="error" />
            </div>
          )}
        </SidePanel>
      ) : null}
      {inactivate ? (
        <Modal
          role="alertdialog"
          label="Confirmar inactivación"
          width={500}
          onClose={() => {
            setInactivate(null);
            setInactivateImpact(null);
            setInactivateKey(null);
          }}
          // Revisión en vivo 2026-09-22, puntos #15 y #18: era la quinta
          // estructura de modal (`<h2>`/`<p>`/`<label>` crudos, botones
          // `.btn`/`.btn-primary`, y un `<select>` sin ningún estilo que se
          // dibujaba 100% nativo). Ahora: regiones fijas del `Modal`,
          // `PortalButton` —con el tono de peligro en la acción destructiva—
          // y el `<select>` con `selectStyle`.
          // Punto #21 (misma revisión): encabezado común de diálogos.
          header={
            <DialogHeader
              eyebrow="Gestión de miembros"
              title="¿Inactivar miembro?"
            />
          }
          footer={
            <div
              style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}
            >
              <PortalButton
                type="button"
                tone="secondary"
                size="small"
                onClick={() => {
                  setInactivate(null);
                  setInactivateImpact(null);
                  setInactivateKey(null);
                }}
              >
                Cancelar
              </PortalButton>
              <PortalButton
                type="button"
                tone="danger"
                size="small"
                disabled={!reason || mutations.inactivate.isPending}
                onClick={confirmInactivate}
              >
                {inactivateImpact ? "Confirmar inactivación" : "Sí, inactivar"}
              </PortalButton>
            </div>
          }
        >
          {inactivateImpact ? (
            <AlertText style={{ margin: "0 0 14px" }}>
              Al confirmar, se restarán {inactivateImpact.balanceToSubtract}{" "}
              puntos y se cerrará su conteo.{" "}
              {inactivateImpact.currentProgress === null ||
              inactivateImpact.goal === null
                ? "No se puede determinar su avance hacia el Gran Premio para este miembro."
                : `Perderá su avance de ${inactivateImpact.currentProgress} de ${inactivateImpact.goal} hacia el Gran Premio.`}{" "}
              Los referidos, consumos e historial se conservan. Al validar su
              código verá el aviso de membresía inactiva y el acceso a su
              asesora; no se emitirá una sesión.
            </AlertText>
          ) : (
            <p style={{ margin: "0 0 14px" }}>
              Se restará su saldo de puntos y se cerrará su conteo del Gran
              Premio. Los referidos, consumos e historial se conservan. Al
              validar su código verá el aviso de membresía inactiva y el acceso
              a su asesora; no se emitirá una sesión.
            </p>
          )}
          <Field label="Motivo de inactivación">
            <select
              aria-label="Motivo de inactivación"
              value={reason}
              style={selectStyle}
              onChange={(e) => setReason(e.target.value)}
            >
              <option value="">Selecciona un motivo</option>
              {(inactivationReasons.data ?? []).map((entry) => (
                <option key={entry.id} value={entry.id}>
                  {typeof entry.name === "string" ? entry.name : entry.name.es}
                </option>
              ))}
            </select>
          </Field>
        </Modal>
      ) : null}
      {reactivation ? (
        <Modal
          role="alertdialog"
          label="Reactivar membresía"
          width={500}
          onClose={() => setReactivation(null)}
          // Misma quinta estructura que "¿Inactivar miembro?" (punto #15), en
          // el mismo archivo: `<h2>`/`<p>`/`<label>` crudos y, además, dos
          // `<input>` sin ningún estilo — se dibujaban nativos igual que el
          // `<select>` del punto #18.
          // Punto #21 (misma revisión): encabezado común de diálogos.
          header={
            <DialogHeader
              eyebrow="Gestión de miembros"
              title="Reactivar membresía"
            />
          }
          footer={
            <div
              style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}
            >
              <PortalButton
                type="button"
                tone="secondary"
                size="small"
                onClick={() => setReactivation(null)}
              >
                Cancelar
              </PortalButton>
              <PortalButton
                type="button"
                size="small"
                disabled={
                  !reactivationDate ||
                  !reactivationReason ||
                  mutations.reactivate.isPending
                }
                onClick={() =>
                  mutations.reactivate.mutate(
                    {
                      id: reactivation.id,
                      input: {
                        purchaseDate: reactivationDate,
                        reason: reactivationReason,
                      },
                    },
                    {
                      onSuccess: () => {
                        setReactivation(null);
                        toast.show("Membresía reactivada con saldo en cero.");
                      },
                      onError: (e) =>
                        reportError(toast, e, MEMBER_ERROR_MESSAGES),
                    },
                  )
                }
              >
                Confirmar reactivación
              </PortalButton>
            </div>
          }
        >
          <p style={{ margin: "0 0 14px" }}>
            El saldo permanece en cero y el conteo inicia con la fecha de compra
            del vehículo nuevo.
          </p>
          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            <Field label="Fecha de compra del vehículo nuevo">
              <input
                aria-label="Fecha de compra del vehículo nuevo"
                type="date"
                style={inputStyle}
                value={reactivationDate}
                onChange={(e) => setReactivationDate(e.target.value)}
              />
            </Field>
            <Field label="Motivo">
              <input
                aria-label="Motivo de reactivación"
                style={inputStyle}
                value={reactivationReason}
                onChange={(e) => setReactivationReason(e.target.value)}
              />
            </Field>
          </div>
        </Modal>
      ) : null}
    </div>
  );
}
